import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import type { RouterContext } from "@/app/router-context";
import { AppSidebar } from "@/components/shell/app-sidebar";
import { AppTopbar } from "@/components/shell/app-topbar";
import { CommandPalette } from "@/components/shell/command-palette";
import { EmptyState } from "@/components/states/state-views";
import { Button } from "@/components/ui/button";
import { alertsFixture } from "@/features/operations/mock-data";
import { useTheme } from "@/lib/theme";

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound
});

const NAV_STORAGE_KEY = "sh.nav-collapsed";

function readCollapsed(): boolean {
  try {
    return globalThis.localStorage?.getItem(NAV_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function RootLayout() {
  const { apiBaseUrl } = Route.useRouteContext();
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        globalThis.localStorage?.setItem(NAV_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // 永続化できなくても開閉自体は動かす
      }
      return next;
    });
  }, []);

  // ⌘K / Ctrl+K でどの画面からでもコマンドパレットを開ける（原則 7）
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    globalThis.addEventListener("keydown", onKeyDown);
    return () => globalThis.removeEventListener("keydown", onKeyDown);
  }, []);

  const criticalCount = alertsFixture.filter(
    (alert) => alert.severity === "critical" && !alert.acknowledged
  ).length;

  return (
    <div className="app-shell" data-nav={collapsed ? "collapsed" : "expanded"}>
      <AppSidebar collapsed={collapsed} onToggle={toggleCollapsed} criticalCount={criticalCount} />

      <div className="flex min-w-0 flex-col">
        <AppTopbar
          onOpenCommandPalette={() => setPaletteOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
          environment="Production"
          apiBaseUrl={apiBaseUrl}
        />
        <main className="flex min-w-0 flex-1 flex-col">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

function NotFound() {
  return (
    <EmptyState
      title="ページが見つかりません"
      description="URL が変更されたか、削除された可能性があります。左のナビゲーションから移動してください。"
      action={
        <Button size="sm" render={<Link to="/" />}>
          概況へ戻る
        </Button>
      }
      className="min-h-[60vh]"
    />
  );
}
